import type {
    FilterState,
    FilterType
} from "@/app/(site)/_components/Organisms/ProductGrids/filterReducer"
import {
    getFilterOp,
    SIZE_FILTER_FIELD,
    sortConfig
} from "@/app/(site)/_components/Organisms/ProductGrids/types/options"
import type {
    SearchFilter,
    SortModel
} from "@/lib/depict-client/types/api/ProductListingRequestV3"
import logger from "@/lib/logger/logger-init"

export const mapSearchParamsToFilters = (
    searchParams: { [key: string]: string },
    shouldTransformSize?: boolean,
    generalSizeToSizeMap?: Record<string, string[]>,
    allowedFacets?: Set<string>
): FilterState => {
    const filters: FilterType[] = []

    let sort: SortModel | undefined = undefined

    Object.keys(searchParams).forEach(param => {
        if (param === "sort") {
            const sortValue = searchParams[param]?.split("&")
            if (sortValue && sortValue.length === 2) {
                const [field, order] = sortValue
                const matchedSortConfig = Object.values(sortConfig).find(
                    config => config.field === field && config.order === order
                )

                if (matchedSortConfig) {
                    sort = matchedSortConfig
                } else {
                    logger.error(
                        `No matching sort configuration found for: ${field}, ${order}`
                    )
                }
            }
        } else if (allowedFacets?.has(param)) {
            let values = searchParams[param].split("&")

            if (
                param === SIZE_FILTER_FIELD &&
                shouldTransformSize &&
                generalSizeToSizeMap
            ) {
                values = values.flatMap(
                    generalSize => generalSizeToSizeMap[generalSize] || [generalSize]
                )
                values = Array.from(new Set(values))
            }

            filters.push({
                data: values,
                field: param,
                op: getFilterOp(param)
            })
        }
    })

    return { filters, sort: sort !== undefined ? sort : undefined }
}

export const updateQueryParamsWithSelectedOptions = (
    router: any,
    pathname: string,
    filterState: FilterType[],
    sort: SortModel | undefined // Add the sort parameter
) => {
    const searchParams = new URLSearchParams(router.query)

    // Handle filter options
    filterState.forEach(option => {
        const { field, data } = option
        if (data && data.length > 0) {
            searchParams.set(field, data.join("&"))
        } else {
            searchParams.delete(field)
        }
    })

    // Handle sort option
    if (sort) {
        searchParams.set("sort", `${sort.field}&${sort.order}`)
    } else {
        searchParams.delete("sort") // Remove sort if none is specified
    }

    // Push updated params to the router
    router.push(`${pathname}?${searchParams.toString()}`, { shallow: true })
}

export const transformedFilterOptions = (
    filterState: FilterType[],
    generalSizeToSizeMap?: Record<string, string[]>
) => {
    return filterState.map(option => ({
        field: option.field,
        op: option.op as SearchFilter["op"],
        data: Array.isArray(option.data)
            ? option.field === SIZE_FILTER_FIELD && generalSizeToSizeMap
                ? option.data.flatMap(dataItem => generalSizeToSizeMap[dataItem] || [])
                : option.data
            : [option.data],
        id: option.field
    }))
}
